import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import {
  SceneShell,
  SlideChip,
  SlideTitle,
  Card3D,
  Punchline,
  DefBox,
  PlainBox,
  Pill,
  SnapshotFrame,
  glow,
  spAppear,
} from "./shared";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/* tiny UI atoms */
const MiniBtn: React.FC<{ label: string; c: string }> = ({ label, c }) => (
  <span
    style={{
      fontSize: 12,
      fontFamily: FONTS.mono,
      fontWeight: 700,
      color: c,
      border: `1px solid ${c}88`,
      borderRadius: 6,
      padding: "3px 9px",
    }}
  >
    {label}
  </span>
);

/* ════════════ Slide 19 — UI #1 chat nudge ════════════ */
export const S19Nudge: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPink;
  const ap = spAppear(frame, fps);
  const nudgeSp = spring({ frame: frame - 46, fps, config: SPRING.punch });
  const nudgePop = interpolate(nudgeSp, [0, 1], [0, 1], clamp);

  const skills = [
    { name: "entity-disambiguation-for-glean", kind: "persona-rule" },
    { name: "cite-source-per-claim", kind: "persona-rule" },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={19} color={accent} appear={ap} label="UI changes" />
      <SlideTitle
        text="UI change #1 — the chat nudge"
        subtitle="Learning becomes visible, right inside the conversation"
        color={accent}
        appear={ap}
      />

      {/* chat window mock */}
      <div
        style={{
          position: "absolute",
          top: 296,
          left: 120,
          width: 770,
          height: 430,
          opacity: spAppear(frame, fps, 12),
          transform: `perspective(1500px) rotateY(12deg)`,
          transformStyle: "preserve-3d",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 18,
            background: COLORS.bgCardLight,
            border: `2px solid ${accent}`,
            boxShadow: `0 0 44px ${accent}44, 0 26px 50px rgba(0,0,0,0.6)`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              height: 44,
              background: COLORS.bgCard,
              borderBottom: `1.5px solid ${accent}44`,
              display: "flex",
              alignItems: "center",
              padding: "0 18px",
              gap: 10,
              fontSize: 16,
              fontFamily: FONTS.mono,
              color: COLORS.textSecondary,
            }}
          >
            💬 EAKC Chat <span style={{ color: COLORS.textMuted }}>· General</span>
          </div>
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            {/* user bubble */}
            <div
              style={{
                alignSelf: "flex-end",
                maxWidth: 480,
                background: `${COLORS.neonBlue}22`,
                border: `1.5px solid ${COLORS.neonBlue}77`,
                borderRadius: 14,
                padding: "12px 16px",
                fontSize: 16,
                color: COLORS.textSecondary,
                opacity: spAppear(frame, fps, 24),
              }}
            >
              Ran "Daily Glean vs EAKC" — the email missed a few big announcements.
            </div>
            {/* nudge card */}
            <div
              style={{
                opacity: nudgePop,
                transform: `scale(${0.85 + nudgePop * 0.15}) translateZ(${nudgePop * 60}px)`,
                background: `linear-gradient(140deg, ${accent}26, ${COLORS.bgCard}f4)`,
                border: `2.4px solid ${accent}`,
                borderRadius: 16,
                padding: 18,
                boxShadow: `0 0 40px ${accent}66`,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.heading,
                  fontWeight: 800,
                  color: accent,
                  textShadow: glow(accent),
                  marginBottom: 12,
                }}
              >
                ✦ EAKC learned 2 skills from this run · auto-applied
              </div>
              {skills.map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 12px",
                    background: `${COLORS.bg}cc`,
                    borderRadius: 10,
                    marginBottom: i === 0 ? 8 : 0,
                    opacity: spAppear(frame, fps, 58 + i * 10),
                  }}
                >
                  <span style={{ fontSize: 22 }}>🧩</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontFamily: FONTS.mono, color: COLORS.textPrimary, fontWeight: 700 }}>
                      {s.name}
                    </div>
                    <div style={{ fontSize: 11, fontFamily: FONTS.mono, color: COLORS.textMuted }}>
                      kind: {s.kind}
                    </div>
                  </div>
                  <MiniBtn label="Read ▸" c={accent} />
                  <MiniBtn label="Undo" c={COLORS.neonCyan} />
                  <MiniBtn label="Pin" c={COLORS.neonYellow} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* snapshot + rules */}
      <SnapshotFrame
        x={1010}
        y={300}
        w={780}
        h={336}
        color={accent}
        frame={frame}
        delay={34}
        fps={fps}
        rotY={-9}
        label="Chat nudge — live in /chat"
        url="eakc.app/chat"
      />
      <div
        style={{
          position: "absolute",
          top: 662,
          left: 1010,
          width: 780,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          opacity: spAppear(frame, fps, 60),
        }}
      >
        <Pill text="dismissible" color={COLORS.neonGreen} size={15} />
        <Pill text="max 1 nudge / turn" color={COLORS.neonCyan} size={15} />
        <Pill text="no approval button" color={COLORS.neonYellow} size={15} />
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="When EAKC learns something, it tells you right there in chat — read it, undo it, or pin it. Most days you just scroll past."
          appear={spAppear(frame, fps, 80)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 20 — UI #2 /admin/learned ════════════ */
export const S20Learned: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonBlue;
  const ap = spAppear(frame, fps);

  const tabs = [
    { t: "Active", n: "49", on: true },
    { t: "Recent", n: "5", on: false },
    { t: "Stale", n: "12", on: false },
    { t: "Archived", n: "", on: false },
    { t: "Pinned", n: "", on: false },
    { t: "Awaiting approval", n: "2", on: false },
  ];
  const rows = [
    { name: "entity-disambiguation-for-glean", kind: "persona-rule", c: COLORS.neonBlue },
    { name: "cite-source-per-claim", kind: "persona-rule", c: COLORS.neonGreen },
    { name: "research-glean-via-deep-research", kind: "plan-template", c: COLORS.neonCyan },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={20} color={accent} appear={ap} label="UI changes" />
      <SlideTitle
        text="UI change #2 — /admin/learned"
        subtitle="One home to read, pin, archive and undo every skill"
        color={accent}
        appear={ap}
      />

      {/* panel mock */}
      <div
        style={{
          position: "absolute",
          top: 298,
          left: 116,
          width: 940,
          height: 460,
          opacity: spAppear(frame, fps, 12),
          transform: `perspective(1600px) rotateY(10deg)`,
          borderRadius: 18,
          background: COLORS.bgCardLight,
          border: `2px solid ${accent}`,
          boxShadow: `0 0 44px ${accent}44, 0 26px 50px rgba(0,0,0,0.6)`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 44,
            background: COLORS.bgCard,
            display: "flex",
            alignItems: "center",
            padding: "0 18px",
            fontSize: 16,
            fontFamily: FONTS.mono,
            color: COLORS.textSecondary,
            borderBottom: `1.5px solid ${accent}44`,
          }}
        >
          🗂️ /admin/learned
        </div>
        {/* tabs */}
        <div style={{ display: "flex", gap: 8, padding: "14px 18px", flexWrap: "wrap" }}>
          {tabs.map((tb, i) => (
            <div
              key={i}
              style={{
                fontSize: 14,
                fontFamily: FONTS.mono,
                fontWeight: 700,
                padding: "7px 14px",
                borderRadius: 8,
                color: tb.on ? COLORS.bg : COLORS.textMuted,
                background: tb.on ? accent : `${accent}14`,
                border: `1.4px solid ${tb.on ? accent : `${accent}44`}`,
                opacity: spAppear(frame, fps, 22 + i * 5),
              }}
            >
              {tb.t}
              {tb.n && ` (${tb.n})`}
            </div>
          ))}
        </div>
        {/* rows */}
        <div style={{ padding: "4px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                background: `${COLORS.bg}cc`,
                borderRadius: 12,
                border: `1.4px solid ${r.c}55`,
                opacity: spAppear(frame, fps, 44 + i * 12),
                transform: `translateX(${(1 - spAppear(frame, fps, 44 + i * 12)) * -40}px)`,
              }}
            >
              <span style={{ fontSize: 26 }}>📌</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontFamily: FONTS.mono, fontWeight: 800, color: COLORS.textPrimary }}>
                  {r.name}
                </div>
                <div style={{ fontSize: 13, fontFamily: FONTS.mono, color: COLORS.textMuted, marginTop: 3 }}>
                  kind: {r.kind} · auto-applied · 2026-05-23
                </div>
              </div>
              <MiniBtn label="Read ▸" c={r.c} />
              <MiniBtn label="Pin" c={COLORS.neonYellow} />
              <MiniBtn label="Archive" c={COLORS.neonOrange} />
            </div>
          ))}
        </div>
      </div>

      {/* snapshot — skill reader */}
      <SnapshotFrame
        x={1100}
        y={300}
        w={700}
        h={456}
        color={COLORS.neonPurple}
        frame={frame}
        delay={40}
        fps={fps}
        rotY={-10}
        label="Skill reader — body · diff · Pin / Archive / Undo"
        url="eakc.app/admin/learned/…"
      />

      <Punchline
        text={
          <>
            One pane to <span style={{ color: accent }}>read</span> everything it
            has learned.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 84)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 21 — UI #3 mission-run skill timeline ════════════ */
export const S21Timeline: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const steps = [
    { t: "step-0 · Research Glean (web)", skills: "4 skills · +45 tok", c: COLORS.neonBlue, cost: "$0.18" },
    { t: "step-1 · Scan EAKC KB", skills: "2 skills · +21 tok", c: COLORS.neonCyan, cost: "$0.04" },
    { t: "step-2 · Compare & merge", skills: "1 skill · +14 tok", c: COLORS.neonPurple, cost: "$0.21" },
    { t: "step-3 · Send (deterministic)", skills: "0 skills", c: COLORS.neonGreen, cost: "$0.00" },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={21} color={accent} appear={ap} label="UI changes" />
      <SlideTitle
        text="UI change #3 — mission skill timeline"
        subtitle="Per step: which skills ran, and what each one cost"
        color={accent}
        appear={ap}
      />

      {/* 3D timeline */}
      <div style={{ position: "absolute", top: 308, left: 150, width: 900, perspective: 1500 }}>
        <div style={{ transform: "rotateY(11deg) rotateX(4deg)", transformStyle: "preserve-3d" }}>
          {steps.map((s, i) => {
            const sp = spring({ frame: frame - 16 - i * 14, fps, config: SPRING.bouncy });
            const op = interpolate(sp, [0, 1], [0, 1], clamp);
            const tx = interpolate(sp, [0, 1], [-120, 0], clamp);
            return (
              <div key={i} style={{ display: "flex", gap: 18, marginBottom: 18, opacity: op }}>
                {/* node + line */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: s.c,
                      boxShadow: `0 0 18px ${s.c}`,
                      flexShrink: 0,
                    }}
                  />
                  {i < steps.length - 1 && (
                    <div style={{ width: 3, flex: 1, background: `${s.c}66`, marginTop: 4 }} />
                  )}
                </div>
                {/* card */}
                <div
                  style={{
                    width: 800,
                    transform: `translateX(${tx}px) translateZ(${i * 12}px)`,
                    borderRadius: 14,
                    background: `linear-gradient(135deg, ${s.c}24, ${COLORS.bgCard}f0)`,
                    border: `2px solid ${s.c}`,
                    boxShadow: `0 0 26px ${s.c}44`,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 20, fontFamily: FONTS.mono, fontWeight: 800, color: s.c }}>{s.t}</div>
                    <div style={{ fontSize: 15, fontFamily: FONTS.body, color: COLORS.textMuted, marginTop: 4 }}>
                      🧩 {s.skills}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontFamily: FONTS.mono,
                      fontWeight: 900,
                      color: COLORS.neonGreen,
                      textShadow: glow(COLORS.neonGreen),
                    }}
                  >
                    {s.cost}
                  </div>
                </div>
              </div>
            );
          })}
          <div
            style={{
              marginLeft: 48,
              width: 800,
              opacity: spAppear(frame, fps, 78),
              fontSize: 18,
              fontFamily: FONTS.mono,
              color: COLORS.textSecondary,
              padding: "12px 22px",
              borderRadius: 12,
              background: `${accent}14`,
              border: `1.6px dashed ${accent}`,
            }}
          >
            Totals: $0.43 · 7 skills injected · 3 new skills proposed [View ▸]
          </div>
        </div>
      </div>

      {/* snapshot */}
      <SnapshotFrame
        x={1110}
        y={306}
        w={680}
        h={446}
        color={accent}
        frame={frame}
        delay={44}
        fps={fps}
        rotY={-10}
        label="Mission run — skill timeline"
        url="eakc.app/admin/missions/…"
      />

      <div style={{ position: "absolute", bottom: 52, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="For every mission step you can see exactly which lessons were used and what they cost. No black box."
          appear={spAppear(frame, fps, 86)}
          width={1140}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 22 — UI #4 memory + token inspector ════════════ */
export const S22Inspector: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPurple;
  const ap = spAppear(frame, fps);

  const layers = [
    { label: "Base persona", tok: 1240, c: COLORS.neonBlue },
    { label: "Attached skills (4)", tok: 428, c: COLORS.neonPink },
    { label: "Agent memory", tok: 312, c: COLORS.neonCyan },
    { label: "Retrieved chunks", tok: 2154, c: COLORS.neonOrange },
    { label: "Conversation history", tok: 1890, c: COLORS.neonPurple },
    { label: "Current message", tok: 146, c: COLORS.neonGreen },
  ];
  const maxTok = 2154;
  const maxBarW = 540;

  return (
    <SceneShell accent={accent}>
      <SlideChip n={22} color={accent} appear={ap} label="UI changes" />
      <SlideTitle
        text="UI change #4 — memory & token inspector"
        subtitle="Line-item visibility into what every answer actually costs"
        color={accent}
        appear={ap}
      />

      {/* stacked layer bars */}
      <div style={{ position: "absolute", top: 306, left: 130, width: 880 }}>
        {layers.map((l, i) => {
          const sp = spring({ frame: frame - 16 - i * 10, fps, config: SPRING.gentle });
          const w = interpolate(sp, [0, 1], [0, (l.tok / maxTok) * maxBarW], clamp);
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 13,
                opacity: op,
              }}
            >
              <div
                style={{
                  width: 220,
                  fontSize: 17,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  textAlign: "right",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {l.label}
              </div>
              <div style={{ position: "relative", width: 646, height: 40, flexShrink: 0 }}>
                <div
                  style={{
                    width: w,
                    height: "100%",
                    borderRadius: 9,
                    background: `linear-gradient(90deg, ${l.c}cc, ${l.c}55)`,
                    border: `1.6px solid ${l.c}`,
                    boxShadow: `0 0 18px ${l.c}66`,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: w + 14,
                    top: 0,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    fontSize: 17,
                    fontFamily: FONTS.mono,
                    fontWeight: 800,
                    color: l.c,
                    whiteSpace: "nowrap",
                  }}
                >
                  {l.tok} tok
                </div>
              </div>
            </div>
          );
        })}
        <div
          style={{
            marginTop: 10,
            marginLeft: 234,
            fontSize: 18,
            fontFamily: FONTS.mono,
            color: COLORS.textPrimary,
            opacity: spAppear(frame, fps, 80),
          }}
        >
          Total input <b style={{ color: accent }}>6,170 tok</b> · ≈ $0.0009
        </div>
      </div>

      {/* right column — coach + caching definition */}
      <div style={{ position: "absolute", top: 312, right: 120, width: 640 }}>
        <Card3D w={640} h={210} color={COLORS.neonAmber} frame={frame} delay={48} fps={fps} inline rotY={-8}>
          <div
            style={{
              fontSize: 16,
              fontFamily: FONTS.mono,
              fontWeight: 800,
              letterSpacing: 2,
              color: COLORS.neonAmber,
            }}
          >
            💡 WHY THIS MUCH?
          </div>
          <div
            style={{
              fontSize: 18,
              fontFamily: FONTS.body,
              color: COLORS.textSecondary,
              marginTop: 10,
              lineHeight: 1.42,
            }}
          >
            Retrieved chunks dominate at 35%. Pin a glossary skill so the agent
            recognises "EAKC" without retrieval — saves ≈ 800 tokens per turn.
          </div>
        </Card3D>
        <div style={{ marginTop: 18 }}>
          <DefBox
            term="Prompt caching"
            body="Re-billing a repeated prompt prefix at roughly a tenth of the cost for a fixed window — powerful, but only where the provider supports it."
            color={accent}
            appear={spAppear(frame, fps, 64)}
            width={640}
          />
        </div>
      </div>

      <Punchline
        text={
          <>
            Cost stops being a <span style={{ color: accent }}>black box</span>.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 92)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 23 — Chat: the past ════════════ */
export const S23ChatPast: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonRed;
  const ap = spAppear(frame, fps);

  const chips = ["Researcher", "Architect", "Reviewer", "Writer", "Analyst", "Planner"];
  const problems = [
    "You had to add an agent before asking anything",
    "Adding several agents did nothing — a dead control",
    "Every tool was granted one at a time",
    "No code execution — numbers were guessed, not computed",
  ];

  return (
    <SceneShell accent={accent} dark>
      <SlideChip n={23} color={accent} appear={ap} label="Chat" />
      <SlideTitle
        text="Chat — the past"
        subtitle="Multi-agent by default — decision paralysis on the first screen"
        color={accent}
        appear={ap}
      />

      {/* cluttered chat */}
      <div
        style={{
          position: "absolute",
          top: 300,
          left: 130,
          width: 800,
          height: 430,
          opacity: spAppear(frame, fps, 12),
          transform: `perspective(1500px) rotateY(10deg)`,
          borderRadius: 18,
          background: COLORS.bgCardLight,
          border: `2px solid ${accent}`,
          boxShadow: `0 0 40px ${accent}44`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 44,
            background: COLORS.bgCard,
            display: "flex",
            alignItems: "center",
            padding: "0 16px",
            fontSize: 15,
            fontFamily: FONTS.mono,
            color: COLORS.textMuted,
          }}
        >
          💬 Chat — pick your agents to begin
        </div>
        {/* co-pilot chips */}
        <div style={{ padding: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {chips.map((c, i) => (
            <div
              key={i}
              style={{
                fontSize: 14,
                fontFamily: FONTS.mono,
                padding: "8px 14px",
                borderRadius: 999,
                border: `1.5px solid ${COLORS.neonOrange}`,
                color: COLORS.neonOrange,
                background: `${COLORS.neonOrange}14`,
                opacity: spAppear(frame, fps, 22 + i * 6),
                transform: `rotate(${(i % 2 ? 1 : -1) * 3}deg)`,
              }}
            >
              @{c} ✕
            </div>
          ))}
          <div
            style={{
              fontSize: 14,
              fontFamily: FONTS.mono,
              padding: "8px 14px",
              borderRadius: 999,
              border: `1.5px dashed ${COLORS.neonRed}`,
              color: COLORS.neonRed,
              opacity: spAppear(frame, fps, 60),
            }}
          >
            + Add agent (required)
          </div>
        </div>
        {/* greyed input */}
        <div style={{ position: "absolute", bottom: 20, left: 18, right: 18 }}>
          <div
            style={{
              height: 56,
              borderRadius: 12,
              background: COLORS.bg,
              border: `1.5px solid ${COLORS.textMuted}55`,
              display: "flex",
              alignItems: "center",
              padding: "0 16px",
              fontSize: 15,
              fontFamily: FONTS.body,
              color: COLORS.textMuted,
            }}
          >
            🔒 Add an agent first to start typing…
          </div>
        </div>
      </div>

      {/* problems */}
      <div style={{ position: "absolute", top: 312, right: 130, width: 680 }}>
        {problems.map((p, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 14,
              alignItems: "center",
              marginBottom: 16,
              opacity: spAppear(frame, fps, 40 + i * 12),
              transform: `translateX(${(1 - spAppear(frame, fps, 40 + i * 12)) * 50}px)`,
              background: `linear-gradient(135deg, ${accent}18, ${COLORS.bgCard}f0)`,
              border: `2px solid ${accent}88`,
              borderRadius: 14,
              padding: "16px 20px",
            }}
          >
            <div style={{ fontSize: 32, color: accent, fontWeight: 900 }}>✕</div>
            <div style={{ fontSize: 19, fontFamily: FONTS.body, color: COLORS.textSecondary }}>{p}</div>
          </div>
        ))}
      </div>

      <Punchline
        text={
          <>
            Assemble a robot <span style={{ color: accent }}>before</span> you can
            ask a question.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 94)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 24 — Chat: the present + Default agent ════════════ */
export const S24ChatNow: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonGreen;
  const ap = spAppear(frame, fps);

  const snapSp = spring({ frame: frame - 40, fps, config: SPRING.punch });
  const snap = interpolate(snapSp, [0, 1], [0, 1], clamp);

  return (
    <SceneShell accent={accent}>
      <SlideChip n={24} color={accent} appear={ap} label="Chat" />
      <SlideTitle
        text="Chat — the present"
        subtitle="Open chat, ask anything — the Default agent is already attached"
        color={accent}
        appear={ap}
      />

      {/* clean chat */}
      <div
        style={{
          position: "absolute",
          top: 300,
          left: 130,
          width: 800,
          height: 392,
          opacity: spAppear(frame, fps, 12),
          transform: `perspective(1500px) rotateY(10deg)`,
          borderRadius: 18,
          background: COLORS.bgCardLight,
          border: `2px solid ${accent}`,
          boxShadow: `0 0 46px ${accent}55`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: 44,
            background: COLORS.bgCard,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            fontSize: 15,
            fontFamily: FONTS.mono,
            color: COLORS.textSecondary,
          }}
        >
          <span>💬 EAKC Chat</span>
          <span style={{ fontSize: 13, color: COLORS.textMuted }}>⚙ Advanced — multi-agent (off)</span>
        </div>
        <div style={{ padding: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
          <div style={{ fontSize: 60 }}>✨</div>
          <div style={{ fontSize: 22, fontFamily: FONTS.heading, fontWeight: 800, color: COLORS.textPrimary }}>
            Ask anything — zero setup
          </div>
          {/* default persona pill snapping in */}
          <div
            style={{
              opacity: snap,
              transform: `scale(${0.6 + snap * 0.4})`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 22px",
                borderRadius: 999,
                background: `${accent}22`,
                border: `2px solid ${accent}`,
                boxShadow: `0 0 28px ${accent}88`,
                fontSize: 18,
                fontFamily: FONTS.mono,
                fontWeight: 800,
                color: accent,
              }}
            >
              🟢 General ▾ <span style={{ color: COLORS.textMuted, fontWeight: 500 }}>· auto-attached</span>
            </div>
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 20, left: 18, right: 18 }}>
          <div
            style={{
              height: 56,
              borderRadius: 12,
              background: COLORS.bg,
              border: `1.6px solid ${accent}77`,
              display: "flex",
              alignItems: "center",
              padding: "0 16px",
              fontSize: 15,
              fontFamily: FONTS.body,
              color: COLORS.textSecondary,
            }}
          >
            Type your question…
            <span style={{ marginLeft: "auto", color: COLORS.textMuted, fontSize: 13 }}>
              @persona to override one message
            </span>
          </div>
        </div>
      </div>

      {/* right column */}
      <div style={{ position: "absolute", top: 304, right: 120, width: 660 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            "One capable General persona — full toolset + RAG",
            "Single active dropdown — personas are a one-tap upgrade",
            "execute_code now runs in an isolated Railway sandbox",
            "Multi-agent demoted to an opt-in Advanced toggle",
          ].map((t, i) => (
            <div
              key={i}
              style={{
                fontSize: 17,
                fontFamily: FONTS.body,
                color: COLORS.textSecondary,
                display: "flex",
                gap: 10,
                opacity: spAppear(frame, fps, 30 + i * 9),
              }}
            >
              <span style={{ color: accent, fontWeight: 900 }}>✓</span>
              {t}
            </div>
          ))}
        </div>
        <DefBox
          term="Default agent — the General persona"
          body="A per-tenant baseline persona (full standard toolset + execute_code + RAG) that a chat with no explicit persona resolves to automatically. There is never a 'no agent' state."
          color={accent}
          appear={spAppear(frame, fps, 60)}
          width={660}
        />
      </div>

      <div style={{ position: "absolute", bottom: 50, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="Now you open chat and just ask. A capable helper is already there — you never set one up. Personas became a one-tap upgrade, not a chore."
          appear={spAppear(frame, fps, 78)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
